import {Form, FormFieldValidationResult, IFormField} from '@mintdata-wsp/common/components/form/Form';
import {BaseComponentWithContext} from '@mintdata/mint-kit/api/components/BaseComponentWithContext';
import {GUID} from '@mintdata/mint-kit/api/models/GUID';
import {Popover} from '@mintdata/mint-kit/impl/components/popover/Popover';
import {PopupAnchor, PopupSize, SourceAnchor} from '@mintdata/mint-kit/impl/components/popover/PopupTypes';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';
import {IStyles, Styles} from '@mintdata/mint-kit/impl/util/Styles';
import bind from 'bind-decorator';
import * as classnames from 'classnames/bind';
import * as React from 'react';

const formFieldStyles = require('@mintdata-wsp/common/components/formField/FormField.less');

export enum ErrorPopupSide
{
    Right = 'rightError',
    Bottom = 'bottomError',
}

export type FormFieldContentRenderer<T> = (state:IFormFieldState<T>) => JSX.Element | Array<JSX.Element>;
export type FormFieldValidationFn<T> = (newValue:T, state:IFormFieldState<T>, form?:Form) => FormFieldValidationResult | Promise<FormFieldValidationResult>;

export interface IFormFieldProps<T>
{
    content?:FormFieldContentRenderer<T>;
    className?:string;
    initialValue?:T;
    isInitiallyFocused?:boolean;
    isReadOnly?:boolean;
    isDisabled?:boolean;
    validationFn?:FormFieldValidationFn<T>;
    errorPopupSide?:ErrorPopupSide;
    formKey?:string;
    form?:Form;
    theme?:IStyles;
}

export interface IFormFieldState<T>
{
    value:T;
    validationResult:FormFieldValidationResult;
    isFocused:boolean;
    isDirty:boolean;
    isSubmitted:boolean;
    isEditedAfterFocus:boolean;
    styles:IStyles;
}

export class FormField<T>
    extends BaseComponentWithContext<IFormFieldProps<T>, IFormFieldState<T>>
    implements IFormField<T>
{
    private readonly _errorPopupId:GUID = IdUtil.newGUID();

    public constructor(props:IFormFieldProps<T>)
    {
        super(props);
    }

    @bind
    public async validate(isSubmit:boolean = false):Promise<FormFieldValidationResult>
    {
        const {value} = this.state;
        const {form, formKey} = this.props;

        const validationResult:FormFieldValidationResult = await this._checkIsValid(value, isSubmit);

        this.setState({
            validationResult
        });

        if( form && formKey )
            form.onValidated(formKey, value, validationResult);

        return validationResult;
    }

    @bind
    public onSubmit():void
    {
        this.setState({isSubmitted: true});
    }

    @bind
    public getValidationResult():FormFieldValidationResult
    {
        return this.state.validationResult;
    }

    @bind
    public getInitialValue():T
    {
        return this.props.initialValue;
    }

    @bind
    public getValue():T
    {
        return this.state.value;
    }

    @bind
    public focus()
    {
        this.setState({isFocused: true, isEditedAfterFocus: false});

        setTimeout(() => this.validate());
    }

    @bind
    public blur()
    {
        this.setState({isFocused: false, isEditedAfterFocus: false});

        setTimeout(() => this.validate());
    }

    @bind
    public reset(value?:T):void
    {
        const {initialValue} = this.props;

        this.setState({
            value: value ? value : initialValue,
            validationResult: new FormFieldValidationResult(true, ''),
            isDirty: false,
            isEditedAfterFocus: false,
            isSubmitted: false,
        });
    }

    @bind
    public async change(newValue:T):Promise<FormFieldValidationResult>
    {
        const {formKey, form} = this.props;

        this.setState({
            value: newValue,
            isDirty: true,
            isSubmitted: false,
            isEditedAfterFocus: true,
        });

        const validationResult:FormFieldValidationResult = await this._checkIsValid(newValue, false, true);

        this.setState({validationResult});

        if( form && formKey )
        {
            form.onValidated(formKey, newValue, validationResult);
            form.onFieldChanged(formKey);
            form.removeServerError(formKey);
        }

        return validationResult;
    }

    @bind
    public userInput(newValue:T):void
    {
        this.setState({
            value: newValue,
            isDirty: true,
        });
    }

    private async _checkIsValid(value:T, isSubmit:boolean = false, isChange:boolean = false):Promise<FormFieldValidationResult>
    {
        const {validationFn, form} = this.props;

        return validationFn && typeof validationFn === 'function'
            ? await validationFn(
                value,
                {
                    value: this.state.value,
                    isFocused: this.state.isFocused,
                    isDirty: this.state.isDirty,
                    isSubmitted: isSubmit ? true : isChange ? false : this.state.isSubmitted,
                    isEditedAfterFocus: isChange ? true : this.state.isEditedAfterFocus,
                    validationResult: this.state.validationResult,
                    styles: this.state.styles
                },
                form
            ) : new FormFieldValidationResult(true, '');
    }

    private _renderPopupArrow():JSX.Element
    {
        const {errorPopupSide} = this.props;
        const {styles} = this.state;
        const cx = classnames.bind(styles);

        if( errorPopupSide === ErrorPopupSide.Right )
            return <div className={cx('arrowLeft')} />;
        else
            return <div className={cx('arrowUp')} />;
    }

    private _renderErrorMessage():JSX.Element
    {
        const {errorPopupSide} = this.props;
        const {validationResult: {isValid, errorMessage}, styles} = this.state;
        const cx = classnames.bind(styles);

        if( isValid || !errorMessage )
            return null;

        const lines = errorMessage.split(/\n|\|/);

        return <Popover id={this._errorPopupId} isModal={false} ignoreEsc={true}>
            <div className={cx('popupContainer', errorPopupSide)}>

                {this._renderPopupArrow()}
                <div className={cx('errorMessage')}>
                    {
                        lines.map((line:string, idx:number) =>
                        {
                            const text:string = line ? line.trim() : '';
                            return <div key={idx} className={cx('errorLine')}>{text}</div>;
                        })
                    }
                </div>
            </div>
        </Popover>;
    }

    @bind
    private _computePopupPositioner()
    {
        return (ref:HTMLElement):void =>
        {
            if( !ref )
                return;

            const {errorPopupSide} = this.props;
            const {popupManager} = this.context;

            let popupFn:any;

            if( errorPopupSide === ErrorPopupSide.Right )
                popupFn = popupManager.position(
                    this._errorPopupId,
                    SourceAnchor.RightMiddle,
                    PopupAnchor.LeftMiddle
                );
            else
                popupFn = popupManager.position(
                    this._errorPopupId,
                    SourceAnchor.LeftBottom,
                    PopupAnchor.LeftTop,
                    PopupSize.WidthOfSource
                );

            popupFn(ref);
        };
    }

    @bind
    private _onKeyPress(event:React.KeyboardEvent<HTMLDivElement>)
    {
        const {form} = this.props;

        if( form )
        {
            if( event.key.toLowerCase() === 'enter' )
                form.submit();
        }
    }

    private _initStyles(styles:IStyles):void
    {
        this.setState({
            styles: Styles.unionStyles(styles, formFieldStyles)
        });
    }

    public componentWillMount():void
    {
        const {theme, formKey, form} = this.props;

        this._initStyles(theme);
        this.reset();

        if( form )
            form.registerField(formKey, this);
    }

    public componentWillReceiveProps(newProps:IFormFieldProps<T>):void
    {
        const {theme, initialValue} = this.props;

        if( initialValue !== newProps.initialValue )
            this.setState({
                value: newProps.initialValue
            });

        if( theme !== newProps.theme )
            this._initStyles(newProps.theme);
    }

    public componentWillUnmount():void
    {
        const {form, formKey} = this.props;

        if( form )
            form.unregisterField(formKey);
    }

    public render():JSX.Element
    {
        const {content, className, isReadOnly, isDisabled, children} = this.props;
        const {validationResult, isFocused, isDirty, isSubmitted, styles} = this.state;
        const cx = classnames.bind(styles);

        const isValid = validationResult.isValid;
        const isInvalid = !isValid;

        return (
            <div
                className={cx(
                    'formField',
                    className,
                    {
                        isReadOnly,
                        isDisabled,
                        isFocused,
                        isValid,
                        isInvalid
                    }
                )}
                ref={this._computePopupPositioner()}
                onKeyPress={this._onKeyPress}
            >
                {content ? content(this.state) : children}
                {(isDirty || isSubmitted) && this._renderErrorMessage()}
            </div>
        );
    }
}
